import React, { useRef, useEffect } from 'react';
import { Box, Typography, FormHelperText } from '@mui/material';

// Import Quill CSS
import 'quill/dist/quill.snow.css';

const QuillEditor = ({
  value = '',
  onChange,
  label,
  placeholder = 'Start typing...',
  error = false,
  helperText,
  disabled = false,
  required = false,
  validation = {},
  description,
  minHeight = '150px',
  maxHeight = '500px',
  autoExpand = true,
  ...props
}) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const initializedRef = useRef(false);

  // Convert height strings to numbers
  const getHeightValue = (heightStr) => {
    return parseInt(heightStr.replace('px', ''));
  };

  // Initialize Quill ONCE - never re-initialize
  useEffect(() => {
    if (initializedRef.current) return; // Already initialized, skip
    
    let Quill;
    
    // Dynamically import Quill to avoid SSR issues
    const loadQuill = async () => {
      try {
        Quill = (await import('quill')).default;
        
        if (editorRef.current && !quillRef.current) {
          // Configure Quill options
          const options = {
            theme: 'snow',
            placeholder: placeholder,
            readOnly: disabled,
            modules: {
              toolbar: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'align': [] }],
                ['blockquote'],
                ['link', 'image'],
                ['clean']
              ]
            },
            formats: [
              'header', 'bold', 'italic', 'underline', 'strike',
              'list', 'bullet', 'align', 'blockquote',
              'link', 'image'
            ]
          };

          // Create Quill instance
          const quill = new Quill(editorRef.current, options);
          quillRef.current = quill;

          // Set initial content ONCE
          if (value) {
            quill.root.innerHTML = value;
          }

          // Handle content changes
          quill.on('text-change', (delta, oldDelta, source) => {
            // Only trigger onChange for user changes
            if (source === 'user') {
              const html = quill.root.innerHTML;
              if (onChange) {
                onChange(html);
              }
            }

            // Auto-resize functionality
            if (autoExpand) {
              const minHeightValue = getHeightValue(minHeight);
              const maxHeightValue = getHeightValue(maxHeight);
              const scrollHeight = quill.root.scrollHeight;
              
              if (scrollHeight <= maxHeightValue) {
                editorRef.current.style.height = 'auto';
                editorRef.current.style.overflowY = 'hidden';
              } else {
                editorRef.current.style.height = `${maxHeightValue}px`;
                editorRef.current.style.overflowY = 'auto';
              }
            }
          });

          // Auto-resize setup
          if (autoExpand) {
            const minHeightValue = getHeightValue(minHeight);
            const maxHeightValue = getHeightValue(maxHeight);
            
            // Set initial height
            editorRef.current.style.minHeight = `${minHeightValue}px`;
            editorRef.current.style.maxHeight = `${maxHeightValue}px`;
            
            // Initial resize
            const scrollHeight = quill.root.scrollHeight;
            if (scrollHeight <= maxHeightValue) {
              editorRef.current.style.height = 'auto';
              editorRef.current.style.overflowY = 'hidden';
            } else {
              editorRef.current.style.height = `${maxHeightValue}px`;
              editorRef.current.style.overflowY = 'auto';
            }
          }

          initializedRef.current = true; // Mark as initialized
        }
      } catch (error) {
        console.error('Failed to load Quill:', error);
      }
    };

    loadQuill();

    // Cleanup
    return () => {
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, []); // Empty deps - only run once on mount

  // Update readOnly state
  useEffect(() => {
    if (quillRef.current && initializedRef.current) {
      quillRef.current.enable(!disabled);
    }
  }, [disabled]);

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label} {required && '*'}
        </Typography>
      )}
      
      <Box sx={{
        border: error ? '2px solid' : '1px solid',
        borderColor: error ? 'error.main' : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '&:hover': {
          borderColor: error ? 'error.main' : 'primary.main',
        },
        '&:focus-within': {
          borderColor: error ? 'error.main' : 'primary.main',
          boxShadow: error ? '0 0 0 2px rgba(211, 47, 47, 0.2)' : '0 0 0 2px rgba(25, 118, 210, 0.2)',
        },
        '& .ql-container': {
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
        '& .ql-toolbar': {
          border: 'none',
          borderBottom: '1px solid #ddd',
          padding: '8px',
        },
        '& .ql-editor': {
          fontSize: '14px',
          lineHeight: '1.5',
        },
        '& .ql-editor.ql-blank::before': {
          fontStyle: 'normal',
          color: '#999',
        }
      }}>
        <div 
          ref={editorRef}
          style={{
            backgroundColor: 'white',
            minHeight: autoExpand ? minHeight : '200px',
          }}
        />
      </Box>

      {/* Helper Text */}
      {helperText && (
        <FormHelperText error={error} sx={{ mt: 1 }}>
          {helperText}
        </FormHelperText>
      )}

      {/* Description */}
      {description && !helperText && (
        <FormHelperText sx={{ mt: 1 }}>
          {description}
        </FormHelperText>
      )}

      {/* Character Count (if validation has maxLength) */}
      {validation.maxLength && value && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {value.replace(/<[^>]*>/g, '').length} / {validation.maxLength} characters
        </Typography>
      )}
    </Box>
  );
};

export default QuillEditor;
